/**
 * Outbox sync runner — singleton that drains queued mutations one at a
 * time when the browser is online. Triggered on:
 *   - `window.online` event
 *   - `document.visibilitychange` → visible
 *   - a 60s interval while the page is open
 *   - explicit `kick()` after enqueueing a new mutation
 *
 * Hard rules:
 *   - sequential (one in-flight at a time) — Mongo's idempotency on the
 *     remove endpoint is per-batch but ordering still matters for the
 *     optimistic UI overlay (`InventoryPage` looks up the live row by
 *     id after each sync).
 *   - pauses on 401 (session expired) and surfaces a status callback so
 *     the AuthContext can drop the user back to the login screen.
 *   - retries with exponential backoff (max 5 attempts) — after that
 *     the entry stays in the outbox with status="error" and is shown
 *     in the indicator pill as "n failed".
 */
import axios from "axios";
import {
  listOutbox,
  updateOutboxEntry,
  removeOutboxEntry,
  countPendingOutbox,
  OUTBOX_MAX_ATTEMPTS,
} from "./offlineStore";
import { getDevelopmentBearerToken } from "./authSession";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SYNC_INTERVAL_MS = 60 * 1000;

export class SyncRunner {
  constructor() {
    this._userId = null;
    this._running = false;        // a drain is currently in flight
    this._paused = false;          // 401 → operator must re-auth
    this._listeners = new Set();   // status subscribers
    this._timer = null;
    this._generation = 0;
    // Bind handlers so add/removeEventListener cleanly pair.
    this._onOnline = () => this.kick("online-event");
    this._onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        this.kick("visibility");
      }
    };
  }

  /** Begin draining for the given user. Idempotent. */
  start(userId) {
    if (!userId) return;
    if (this._userId === userId && this._timer) return;
    this.stop();
    this._userId = userId;
    this._paused = false;
    if (typeof window !== "undefined") {
      window.addEventListener("online", this._onOnline);
      document.addEventListener("visibilitychange", this._onVisible);
    }
    this._timer = setInterval(() => this.kick("interval"), SYNC_INTERVAL_MS);
    this.kick("start");
  }

  /** Stop draining and detach listeners. */
  stop() {
    this._generation += 1;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this._onOnline);
      document.removeEventListener("visibilitychange", this._onVisible);
    }
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
    this._userId = null;
    this._listeners.clear();
  }

  /** Subscribe to status changes. Returns an unsubscribe function. */
  subscribe(fn) {
    this._listeners.add(fn);
    // Send current snapshot once on subscribe.
    this._emit().catch(() => {});
    return () => this._listeners.delete(fn);
  }

  _isCurrent(owner, generation) {
    return Boolean(owner) && owner === this._userId && generation === this._generation;
  }

  async _emit(owner = this._userId, generation = this._generation) {
    if (!this._isCurrent(owner, generation)) return;
    const entries = await listOutbox(owner);
    if (!this._isCurrent(owner, generation)) return;
    const pending = entries.filter((e) => e.status === "pending" || e.status === "syncing").length;
    const errored = entries.filter((e) => e.status === "error").length;
    const snap = { pending, errored, paused: this._paused, total: pending + errored };
    for (const fn of this._listeners) {
      try { fn(snap); } catch (_) { /* listener bug shouldn't kill runner */ }
    }
  }

  /** Resume after a 401 once the user logs back in. */
  resume() {
    this._paused = false;
    this.kick("resume");
  }

  /** Request a drain. Reentrant-safe — only one drain runs at a time. */
  kick(_reason) {
    if (this._running || this._paused) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    this._drain().catch(() => {}); // never let drain errors escape
  }

  async _drain() {
    if (this._running || !this._userId) return;
    const owner = this._userId;
    const generation = this._generation;
    this._running = true;
    try {
      let entries = await listOutbox(owner);
      if (!this._isCurrent(owner, generation)) return;
      entries = entries.filter((e) => e.userId === owner &&
        (e.status === "pending" || (e.status === "error" && e.attempts < OUTBOX_MAX_ATTEMPTS)));
      if (entries.length === 0) {
        await this._emit(owner, generation);
        return;
      }
      for (const entry of entries) {
        if (!this._isCurrent(owner, generation) || this._paused) break;
        if (typeof navigator !== "undefined" && !navigator.onLine) break;
        await this._processOne(entry, owner, generation);
        await this._emit(owner, generation);
      }
    } finally {
      this._running = false;
      // A replacement session may have requested a drain while this one was
      // awaiting I/O. Only its own captured queue may run next.
      if (this._userId && !this._isCurrent(owner, generation)) this.kick("session-changed");
    }
  }

  async _processOne(queued, owner, generation) {
    if (!this._isCurrent(owner, generation) || queued.userId !== owner) return;
    const entry = { ...queued };
    const token = getDevelopmentBearerToken();
    // Keep the durable entry pending until a current-session outcome exists.
    // A stopped/expired session must not leave a stranded "syncing" entry.
    try {
      await axios.request({
        url: `${API_BASE}${entry.url}`,
        method: entry.method || "POST",
        data: entry.body || {},
        // Cookies can change in another tab. This header can only restrict
        // the authenticated actor; the backend refuses a different user.
        headers: { "X-ShelfLife-Outbox-User": owner,
          ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: true,
        // 15s is generous for slow networks but bounded so a black-hole
        // request can't stall the queue forever.
        timeout: 15000,
      });
      // Success already executed for this captured owner. A later session
      // must not cause that completed mutation to replay on the next login.
      await removeOutboxEntry(owner, entry.id);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 && entry.kind === "remove_inventory") {
        await removeOutboxEntry(owner, entry.id);
        return;
      }
      if (!this._isCurrent(owner, generation)) return;
      const msg = err?.response?.data?.detail || err?.message || "sync failed";
      if (status === 401) {
        // Don't burn an attempt on auth — pause until operator re-auths.
        entry.status = "pending";
        entry.lastError = "session expired";
        await updateOutboxEntry(owner, entry);
        if (this._isCurrent(owner, generation)) this._paused = true;
        return;
      }
      entry.attempts += 1;
      entry.lastError = typeof msg === "string" ? msg : JSON.stringify(msg);
      entry.status = entry.attempts >= OUTBOX_MAX_ATTEMPTS ? "error" : "pending";
      await updateOutboxEntry(owner, entry);
    }
  }

  /** Snapshot of (pending, errored). Cheap — does not trigger a drain. */
  async snapshot() {
    const owner = this._userId;
    const generation = this._generation;
    const empty = { pending: 0, errored: 0, paused: false, total: 0 };
    if (!owner) return empty;
    const entries = await listOutbox(owner);
    if (!this._isCurrent(owner, generation)) return empty;
    const pending = entries.filter((e) => e.status === "pending" || e.status === "syncing").length;
    const errored = entries.filter((e) => e.status === "error").length;
    return { pending, errored, paused: this._paused, total: pending + errored };
  }

  /** Count entries that block a clean logout. */
  async countPending() {
    if (!this._userId) return 0;
    return countPendingOutbox(this._userId);
  }
}

const runner = new SyncRunner();
export default runner;
